import Axhelper from "@/lib/Axhelper";

/**
 * SingleCustomCollectionService - Handles individual custom collection item operations
 * Based on the /custom/ API endpoints for managing items within custom collections
 */
class SingleCustomCollectionService {
  constructor() {
    this.baseUrl = window.RUNTIME_CONFIG.API_URL;
    this.endpoints = {
      custom: `/api/custom/`,
      customFilters: `/api/custom/filters/`,
      customDelete: `/api/custom/`,
      customUpdate: `/api/custom/`
    };
  }

  async createCustomCollectionItem(itemData) {
    try {
      if (!itemData || !itemData.collection) {
        return {
          success: false,
          error: 'Collection name is required',
          data: null
        };
      }

      const response = await Axhelper.sendPostAsync(this.endpoints.custom, itemData);

      if (response.error) {
        return {
          success: false,
          error: response.error,
          data: null
        };
      }

      return {
        success: true,
        error: null,
        data: response.data
      };
    } catch (error) {
      console.error('SingleCustomCollectionService.createCustomCollectionItem error:', error);
      return {
        success: false,
        error: error.message || 'Failed to create custom collection item',
        data: null
      };
    }
  }

  async updateCustomCollectionItem(collectionName, itemId, itemData) {
    try {
      const url = `${this.endpoints.customUpdate}/${encodeURIComponent(collectionName)}/${encodeURIComponent(itemId)}`;
      const response = await Axhelper.sendPutAsync(url, itemData);

      if (response.error) {
        return {
          success: false,
          error: response.error,
          data: null
        };
      }

      return {
        success: true,
        error: null,
        data: response.data
      };
    } catch (error) {
      console.error('SingleCustomCollectionService.updateCustomCollectionItem error:', error);
      return {
        success: false,
        error: error.message || 'Failed to update custom collection item',
        data: null
      };
    }
  }

  async deleteCustomCollectionItem(collectionName, itemId) {
    try {
      if (!collectionName || !itemId) {
        return {
          success: false,
          error: 'Collection name and item ID are required',
          message: null
        };
      }

      const url = `${this.endpoints.customDelete}${encodeURIComponent(collectionName)}/${encodeURIComponent(itemId)}`;
      const response = await Axhelper.sendDeleteAsync(url);

      if (response.error) {
        return {
          success: false,
          error: response.error,
          message: null
        };
      }

      return {
        success: true,
        error: null,
        message: response.data || 'Item deleted successfully'
      };
    } catch (error) {
      console.error('SingleCustomCollectionService.deleteCustomCollectionItem error:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete custom collection item',
        message: null
      };
    }
  }

  async getCustomCollectionItems(collectionName, options = {}) {
    // console.log(`TCL -> SingleCustomCollectionService.js:118 -> SingleCustomCollectionService -> getCustomCollectionItems -> collectionName, options:`, collectionName, options)
    try {
      if (!collectionName) {
        return {
          data: [],
          error: 'Collection name is required',
          totalCount: 0,
          hasMore: false
        };
      }

      const {
        limit,
        sortColumn,
        sortDirection,
        filters = {}
      } = options;

      const url = this._buildFilteredUrl(collectionName, {
        limit,
        sortColumn,
        sortDirection,
        filters
      });

      const response = await Axhelper.sendPostAsync(url, filters);

      if (response.error) {
        return {
          data: [],
          error: response.error,
          totalCount: 0,
          hasMore: false
        };
      }

      // Parse pagination info from headers
      const totalCount = response.headers?.['x-total-count']? parseInt(response.headers['x-total-count'], 10): response.data?.length || 0;
      const hasMore = response.data?.length < totalCount;

      return {
        data: response.data || [],
        error: null,
        totalCount,
        hasMore,
        headers: response.headers
      };
    } catch (error) {
      console.error('SingleCustomCollectionService.getCustomCollectionItems error:', error);
      return {
        data: [],
        error: error.message || 'Failed to fetch custom collection items',
        totalCount: 0,
        hasMore: false
      };
    }
  }

  async getCustomCollectionItemById(collectionName, itemId) {
    // console.log(`TCL -> SingleCustomCollectionService.js:208 -> SingleCustomCollectionService -> getCustomCollectionItemById -> collectionName, itemId:`, collectionName, itemId)
    try {
      if (!collectionName || !itemId) {
        return {
          data: null,
          error: 'Collection name and item ID are required'
        };
      }

      // Use filters endpoint to get specific item
      const url = `${this.endpoints.customFilters}${encodeURIComponent(collectionName)}`;
      let filters = { _id: itemId };
      // console.log(`TCL -> -> url:`, url)
      const response = await Axhelper.sendPostAsync(url, filters);
      // console.log(`TCL -> SingleCustomCollectionService.js:219 -> SingleCustomCollectionService -> getCustomCollectionItemById -> response:`, response)
      
      if (response.error) {
        console.error(`SingleCustomCollectionService.js -> getCustomCollectionItemById -> response.error:`, response.error)  
        return {
          data: null,
          error: response.error
        };
      }

      // Assume response.data is an array with one item
      if (Array.isArray(response.data) && response.data.length > 0) {
        return {
          data: response.data[0],
          error: null
        };
      }

      return {
        data: null,
        error: 'Item not found'
      };
    } catch (error) {
      console.error('SingleCustomCollectionService.getCustomCollectionItemById error:', error);
      return {
        data: null,
        error: error.message || 'Failed to fetch custom collection item'
      };
    }
  }

  _buildFilteredUrl(collectionName, { limit, sortColumn, sortDirection, filters }) {
    let params = [];

    if (limit) {
      params.push(`limit=${limit}`);
    }
    if (sortColumn) {
      params.push(`sortcol=${sortColumn}`);
    }
    if (sortDirection) {
      params.push(`sortdir=${sortDirection}`);
    }

    // Add custom field filters
    // Object.keys(filters).forEach(key => {
    //   if (filters[key] && filters[key].toString().trim()) {
    //     params.push(`${encodeURIComponent(key)}=${encodeURIComponent(filters[key])}`);
    //   }
    // });

    const baseUrl = `${this.endpoints.customFilters}${encodeURIComponent(collectionName)}`;
    return params.length > 0 ? `${baseUrl}?${params.join('&')}` : baseUrl;
  }
}

// Export a singleton instance
export default new SingleCustomCollectionService();